import React, { useCallback, useMemo } from "react";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { getFocusedSection, setShowFormPopup } from "../../../actions/taskSidebar";
import store from "../../../store/index";
import { icon, label, number } from "../../../config";
import { getTriggeredTasks } from "../../../shared/services/automationService";
import { getCurrentActiveProject } from "../../TaskSidebar/sidebar.common";
import { NudgeRow } from "../../Tasks/taskGrid";
import { completeAndForward, openSidebar } from "../../Tasks/tasks.service";

const taskCompleteAction = async (task, userId, onComplete) => {
  if (task) {
    const payload = {
      taskHistoryId: task.taskHistoryId,
      taskId: task.TaskId,
      userId: userId,
      positionId: null,
    };
    const response = await completeAndForward(payload, task.CurrentProject);
    if (response) {
      const freshTasks = await getTriggeredTasks(task.ParentTaskId);
      if (onComplete) onComplete(freshTasks);
    }
  }
};

/**
 * getCompleteIcon Component
 * @author Abhishek Dhillon
 */
export const getCompleteIcon = (props, userId, onComplete) => {
  const isCompleted = props.dataItem.IsCompleted;
  const hasAccessToCurrentFlow = props.dataItem.IsPartOfCurrentProject || props.dataItem.IsPersonalBoard;

  const getButtonClass = () => {
    if (isCompleted) {
      return "btn btn-success btn-sm ml-1";
    }
    if (hasAccessToCurrentFlow) {
      return "btn btn-outline-secondary btn-sm ml-1 cursor-pointer";
    }
    return "btn btn-outline-success btn-sm ml-1";
  };

  return (
    <td {...props.tdProps}>
      <span id='mark-as-complete-icon' className='ml-1'>
        <button
          id={`automation-instance-mark-complete-${props.dataItem?.TaskId ?? props.dataItem?.Id ?? "row"}`}
          type='button'
          className={`${getButtonClass()} mark-as-complete-btn cursor-default`}
          onClick={() => hasAccessToCurrentFlow && !isCompleted && taskCompleteAction(props.dataItem, userId, onComplete)}>
          {isCompleted ? "Closed" : hasAccessToCurrentFlow ? "Complete" : "Completed"}
        </button>
      </span>
    </td>
  );
};

const CustomStep = ({ stepProps, props, assigneeList, currentProjectIndex, lastElement }) => {
  const assignee = assigneeList?.find((assignee) => {
    return assignee.value === stepProps?.Assignee;
  });
  const isActive = currentProjectIndex === stepProps?.index;

  const getAssignee = () => {
    if (assignee) {
      if (assignee?.PhotoLinks?.small) {
        return (
          <img
            className={`user-profile-picture rounded-circle object-fit-cover ${
              isActive ? "user-current-active user-active-img" : stepProps?.IsUserAvailable === number.ONE ? " " : "user-opacity-grayscale-100"
            }`}
            src={assignee?.PhotoLinks?.small}
            alt='Assignee'
          />
        );
      } else {
        return (
          <span
            className={`align-items-center justify-content-center user-current-icon  ${
              isActive ? "user-current-active" : stepProps?.IsUserAvailable === number.ONE ? " " : "opacity-50"
            }`}>
            {icon.ACCOUNT_CIRCLE}
          </span>
        );
      }
    } else {
      return (
        <span
          className={`align-items-center justify-content-center user-current-icon unassigned-user-icon ${
            isActive ? "user-current-active" : stepProps?.IsUserAvailable === number.ONE ? " " : "opacity-50"
          }`}>
          {stepProps?.FlowElementType === number.TWO ? icon.DYNAMIC : icon.UNASSIGNED_USER}
        </span>
      );
    }
  };

  return (
    <Tooltip anchorElement='target' position='bottom' parentTitle='true'>
      <div className={`d-flex align-items-center pr-3 ${!lastElement ? "user-horizontal-line" : ""}`}>
        <div title={assignee?.label ?? label.UNASSIGNED} className='position-relative d-flex'>
          {getAssignee()}
          {Boolean(stepProps.IsApproval) && (
            <span title='Approval' className='user-approval-element position-absolute d-flex align-items-center justify-content-center'>
              {icon.APPROVAL}
            </span>
          )}
          {lastElement && (
            <span title='Last Flow Element' className='user-flag-element position-absolute d-flex align-items-center justify-content-center'>
              {icon.FLAG}
            </span>
          )}
        </div>
      </div>
    </Tooltip>
  );
};

/**
 * renderProfilePicture Component
 * @author Abhishek Dhillon
 */
export const renderProfilePicture = (props, assigneeList) => {
  const { currentProject, currentProjectIndex } = getCurrentActiveProject(
    props.dataItem.taskFlowElements.map((e) => ({ ...e, isCompleted: e.IsCompleted, id: e.Id }))
  );
  return (
    <td {...props.tdProps}>
      <div className='d-flex align-items-center gap-2'>
        {props.dataItem.taskFlowElements.map((e, i) => (
          <div className='position-relative'>
            <CustomStep
              stepProps={{ ...e, index: i }}
              props={props}
              assigneeList={assigneeList}
              currentProjectIndex={currentProjectIndex}
              lastElement={props?.dataItem?.taskFlowElements?.length ? props?.dataItem?.taskFlowElements?.length - 1 === i : 0}
            />
          </div>
        ))}
      </div>
    </td>
  );
};

export const projectTaskIdCell = (props, userId) => {
  return (
    <td {...props.tdProps}>
      <span className='link-text cursor-pointer' onClick={() => openSidebar({ id: props.dataItem.ParentTaskId, userId })}>
        {props.dataItem.ParentTaskCode}
      </span>
    </td>
  );
};

/**
 * Nudge for automation child tasks
 * @author Muskan Thakur
 */
export const AutomationChildNudgeRow = (props) => {
  const { dataItem, onChildTasksRefresh } = props;

  const adaptedDataItem = useMemo(
    () => ({
      ...dataItem,
      Id: dataItem.TaskId,
      CurrentProject: dataItem.CurrentProject,
      IsCompleted: dataItem.IsCompleted === number.ONE,
      isFollowed: dataItem.IsFollowed ?? false,
    }),
    [dataItem]
  );

  const onAfterNudge = useCallback(async () => {
    const parentTaskId = dataItem?.ParentTaskId;
    if (parentTaskId == null || !onChildTasksRefresh) return;
    const fresh = await getTriggeredTasks(parentTaskId);
    onChildTasksRefresh(fresh);
  }, [dataItem?.ParentTaskId, onChildTasksRefresh]);

  const { dataItem: _d, onChildTasksRefresh: _o, tdProps: _t, ...rest } = props;
  return <NudgeRow {...rest} dataItem={adaptedDataItem} onAfterNudge={onAfterNudge} />;
};

export const getColumns = (userId, assigneeList = [], dispatch, onComplete) => {
  return [
    {
      field: "IsCompleted",
      title: " ",
      width: 30,
      cell: (props) => getCompleteIcon(props, userId, onComplete),
    },
    {
      field: "ProjectTaskID",
      title: "Task ID",
      width: 50,
      cell: (props) => {
        const { isForm, submittedFormId, Assignee } = props.dataItem;
        const handleClick = async () => {
          const sidebarProps = { id: props.dataItem.TaskId, userId, taskHistoryId: props.dataItem.taskHistoryId };
          await openSidebar(sidebarProps);
          if (isForm && !submittedFormId && Assignee == userId) {
            const { showTaskSidebar, task } = store.getState().taskSidebar;
            if (showTaskSidebar && Number(task?.taskId) === Number(props.dataItem.TaskId)) {
              dispatch(setShowFormPopup(true));
            }
          }
        };
        return (
          <td {...props.tdProps}>
            <span className='link-text cursor-pointer' onClick={handleClick}>
              {props.dataItem.ProjectTaskID}
            </span>
          </td>
        );
      },
    },
    // { field: "Stage", title: "Status", width: 50 },
    { field: "PhotoLinks", title: "Route", width: 80, cell: (props) => renderProfilePicture(props, assigneeList) },
    {
      field: "DueDate",
      title: "Due Date",
      width: 50,
      format: "{0:dd MMM yy}",
      cell: (props) => {
        let dueClass = new Date(props.dataItem.DueDate) < new Date() ? "red-colour" : "";
        return (
          <td {...props.tdProps}>
            <Tooltip anchorElement='target' position='bottom' parentTitle='true'>
              <div className='d-flex justify-content-between align-items-start'>
                <div>{props.dataItem.DueDate ? <span className={dueClass}>{getDateInFormat(props.dataItem.DueDate)}</span> : ""}</div>
                <div className='d-flex align-items-center justify-content-end gap-2 automation-child-due-actions show-comments'>
                  <span className='task-grid-nudge-icon d-flex align-items-center'>
                    <AutomationChildNudgeRow {...props} onChildTasksRefresh={onComplete} />
                  </span>
                  <span
                    id='show-comments-icon'
                    className={`text-right ${
                      Boolean(props.dataItem.newCommentCount) ? "red" : Boolean(props.dataItem.commentCount) ? "blue" : ""
                    } cursor-pointer d-none`}
                    onClick={async () => {
                      await openSidebar({ id: props.dataItem.TaskId, userId, taskHistoryId: props.dataItem.taskHistoryId });
                      dispatch(getFocusedSection("comments"));
                    }}>
                    {icon.COMMENT}
                    {Boolean(props.dataItem.newCommentCount) && props.dataItem.newCommentCount}
                  </span>
                </div>
              </div>
            </Tooltip>
          </td>
        );
      },
      sortable: true,
    },
  ];
};

export const getDateInFormat = (date) => {
  const dateObj = new Date(date);
  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = dateObj.toLocaleString("default", { month: "short" });
  const year = dateObj.getFullYear();
  return `${day} ${month} ${year}`;
};

export const getPercentage = (value, total) => {
  if (!total) return 0;
  const percentage = (value / total) * 100;
  const decimalPart = percentage - Math.floor(percentage);
  return decimalPart < 0.5 ? Math.floor(percentage) : Math.ceil(percentage);
};

export const getCompletionColor = (percentage) => {
  if (percentage >= 0 && percentage < 20) return "#FF746D";
  if (percentage >= 20 && percentage < 40) return "#C8DE89";
  if (percentage >= 40 && percentage < 60) return "#77AA59";
  if (percentage >= 60 && percentage < 80) return "#599646";
  if (percentage >= 80 && percentage <= 100) return "#3C8233";
  return "#0099BC"; // default color
};

export const CustomDateCell = (props) => <td {...props.tdProps}>{getDateInFormat(props.dataItem.CreatedDate)}</td>;
export const CustomDueDateCell = (props) => <td {...props.tdProps}>{props?.dataItem?.DueDate ? getDateInFormat(props?.dataItem?.DueDate) : ""}</td>;

export const getDateInAPIFormat = (date) => {
  const dateObj = new Date(date);
  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = String(dateObj.getMonth() + 1).padStart(2, "0"); // Months are zero-indexed
  const year = dateObj.getFullYear();
  return `${year}-${month}-${day}`;
};
